import React from "react";
import capitalize from "lodash/capitalize";
import { useParams, usePathname, useSearchParams } from "next/navigation";
import { parse, stringify } from "qs";

import getStrainCuratedList from "@/api/requests/getStrainCuratedList";
import getStrainListFaq from "@/api/requests/getStrainListFaq";
import getStrainListHeader from "@/api/requests/getStrainListHeader";
import getStrainLists, {
  getStrainListsReturn,
  ITEMS_PER_STRAIN_PAGE,
  STRAIN_LIST_PAGE_CATEGORY_FILTERS,
} from "@/api/requests/getStrainLists";
import { getUserLocationData } from "@/api/requests/getUserLocation";
import { STRAIN_USER_REVIEW_DISCLAIMER } from "@/constants/disclaimers";
import { Terpene } from "@/constants/strains";
import { FAQ, StrainListHeaders } from "@/custom-types/Directus";
import { Query } from "@/custom-types/Query";
import { useEventTracker } from "@/hooks/useEventTracker";
import useGetStrainLists from "@/hooks/useGetStrainLists";
import useUserFavorites from "@/hooks/useUserFavorites";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import { normalizeLocationCookie } from "@/utils/getUserLocationFromCookie";
import { redirectIfPageOne } from "@/utils/redirectIfPageOne";
import { getCountryCodeHeader } from "@/utils/requestContext";

import Breadcrumb from "@/components/Breadcrumb";
import Breadcrumbs from "@/components/Breadcrumbs";
import FAQs from "@/components/FAQ";
import { Facet } from "@/components/FilteredGrid";
import MedicalDisclaimers from "@/components/MedicalDisclaimers";
import { MenuPage } from "@/components/MenuFilters/types/MenuFilters";
import MenuFilters from "@/components/MenuFilters/v2";
import { useFilterQueryParams } from "@/components/MenuFilters/v2/hooks";
import StrainList from "@/components/StrainList/StrainList";
import StrainListCountSort from "@/components/StrainList/StrainListCountSort";
import StrainListHead from "@/components/StrainList/StrainListHead";
import StrainListHeader from "@/components/StrainList/StrainListHeader";

type StrainListCategoryPageProps = {
  data: getStrainListsReturn;
  faqs: FAQ["faqs"];
  metadata: StrainListHeaders<string> | null;
};

const StrainListCategoryPage: React.FC<StrainListCategoryPageProps> = ({
  data,
  faqs,
  metadata,
}) => {
  const page = useSearchParams()?.get("page");
  const relativePath = usePathname();
  const params = useParams();

  const { publishEvent } = useEventTracker();

  const [pageName] = params?.slug as string[];

  const { useInitiateFavorites } = useUserFavorites();
  useInitiateFavorites();

  const { data: results, loading } = useGetStrainLists(data);

  const {
    actions: { clearAllFilters, toggleFilter },
  } = useFilterQueryParams();

  const onClearAllClick = (facet?: Facet) => {
    if (!facet) {
      clearAllFilters();
      return;
    }
    toggleFilter(facet.key, facet.value);
  };

  return (
    <>
      <StrainListHead
        title={metadata?.meta_title || metadata?.title || pageName}
        {...(metadata?.meta_description && {
          description: metadata.meta_description,
        })}
      />

      <StrainListHeader
        hero={metadata}
        displayTitle={metadata?.title || pageName}
      />

      <MenuFilters
        availableFilters={results.availableFilters}
        menuPage={MenuPage.strains}
        loading={loading}
        onClearAllClick={onClearAllClick}
      >
        <StrainListCountSort
          totalCount={results.totalCount}
          loading={loading}
        />
        <MedicalDisclaimers
          usDisclaimer={STRAIN_USER_REVIEW_DISCLAIMER}
          className="mb-lg"
        />

        <StrainList
          adPath="/13212770/strain/explore_playlist"
          adTarget="strains"
          loading={loading}
          totalCount={results.totalCount}
          strains={results.strains}
        />
      </MenuFilters>

      {Number(page) <= 1 && (
        <FAQs
          className="mt-xl"
          data={faqs}
          initialShowPanel={true}
          onEntryClick={() => {
            publishEvent({
              action: "Click",
              category: "Strain playlist",
              label: "FAQ",
            });
          }}
        />
      )}

      <Breadcrumbs>
        <Breadcrumb href="/strains/lists">Strains lists</Breadcrumb>
        {relativePath && (
          <Breadcrumb currentLocation href={relativePath}>
            {metadata?.title || pageName}
          </Breadcrumb>
        )}
      </Breadcrumbs>
    </>
  );
};

export const getServerSideProps =
  createGetServerSideProps<StrainListCategoryPageProps>(async (context) => {
    const countryCode = getCountryCodeHeader(context.req.headers);
    const userLocationData = await getUserLocationData(countryCode, context);
    const location = normalizeLocationCookie(userLocationData);

    const { latitude: lat, longitude: lon } = location?.coordinates ?? {};
    const {
      query: { slug, page, sort, ...remainingQuery },
      resolvedUrl,
    } = context;

    const [pageCategory, pageName] = slug as string[];
    // Lots of rules here on this page
    // - Because this page uses dynamic routes, we need to ensure that
    // the slug is only 2 parts long
    // - Since this page won't reached without a pageCategory, we do
    // not have to explicitly check for one
    // - We need to account for curated pages, which should have a
    // pageCategory of 'curated' and a pageName
    // - We need to account for any top level category pages
    // (eg /strains/lists/new-strains), which should have no pageName
    // - We need to account for any filter page (ie /strains/lists/condition/anxiety),
    // which should have a pageCategory that is not 'curated', a valid
    // category and a pageName
    const isTopLevelCategoryPage = !pageName;
    const isCuratedPage = pageCategory === "curated" && pageName;
    const isValidCategory =
      !isCuratedPage &&
      Object.keys(STRAIN_LIST_PAGE_CATEGORY_FILTERS).indexOf(
        `${pageCategory}`,
      ) > -1 &&
      pageName;

    if (
      (slug as string[]).length > 2 ||
      (!isTopLevelCategoryPage && !isCuratedPage && !isValidCategory)
    ) {
      return {
        notFound: true,
      };
    }

    const redirect = redirectIfPageOne(resolvedUrl, page);

    if (redirect) {
      return { redirect };
    }

    const { filter: filterQueryParams = {} } = parse(stringify(remainingQuery));
    const dataCall = getStrainLists(
      filterQueryParams as Query,
      (Number(typeof page === "string" ? page : "1") - 1) *
        ITEMS_PER_STRAIN_PAGE,
      ITEMS_PER_STRAIN_PAGE,
      { lat, lon },
      typeof sort === "string" ? sort : undefined,
      undefined,
      pageCategory,
      pageName,
    );
    const metaDataCall =
      pageCategory === "curated"
        ? getStrainCuratedList(`${pageName}`, 0, 20)
        : getStrainListHeader(
            countryCode,
            isTopLevelCategoryPage ? "category" : String(pageCategory),
            pageName
              ? String(pageName)
              : isTopLevelCategoryPage
                ? String(pageCategory)
                : undefined,
          );
    const faqCall = getStrainListFaq(
      `/strains/lists/${pageCategory}/${pageName}`,
      countryCode,
    );

    const [data, metadata, { faqs }] = await Promise.all([
      dataCall,
      metaDataCall,
      faqCall,
    ]);

    // if page is greater than the pagination available
    // or totalCount is falsy which would indicate the filter does not exist, eg: hops
    const pageDoesNotExist = page
      ? !data.metadata.totalCount ||
        data.metadata.totalCount / ITEMS_PER_STRAIN_PAGE <
          parseInt(page as string) - 1
      : !data.metadata.totalCount;

    // make sure if the page is for a terpene, to always render that page
    // this is to prevent a 404 when there are no strains with a terpene as a top terpene
    const isValidTerpene = Object.values(Terpene).includes(
      capitalize(pageName) as Terpene,
    );

    // Since these are SEO pages, we want to return a 404 if there is no metadata
    if (
      !isValidTerpene &&
      (((isCuratedPage || isTopLevelCategoryPage) && !metadata) ||
        pageDoesNotExist)
    ) {
      return { notFound: true };
    }

    return {
      props: {
        data,
        faqs,
        metadata,
      },
    };
  });

export default StrainListCategoryPage;
